
import React from 'react';
import { Truck, MapPin, Phone, Mail, Facebook, Instagram, Linkedin, MessageCircle, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import XIcon from './XIcon';

const Footer: React.FC = () => {
  const { settings } = useData();

  return (
    <footer className="bg-neutral-950 text-white pt-16 pb-8 border-t border-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand Column */}
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              {settings.logoUrl ? (
                <img src={settings.logoUrl} alt={settings.siteName} className="h-10 w-auto object-contain brightness-0 invert" />
              ) : (
                <>
                  <div className="p-1.5 rounded transform skew-x-[-10deg]" style={{ backgroundColor: settings.logoColor }}>
                    <Truck className="h-6 w-6 text-white skew-x-[10deg]" />
                  </div>
                  <span className="font-display text-3xl font-bold tracking-wider">
                    {settings.siteName.slice(0, 4)}<span style={{ color: settings.logoColor }}>{settings.siteName.slice(4)}</span>
                  </span>
                </>
              )}
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Trusted and Reliable Dealer of Heavy Duty Vehicles in East and Central Africa.
            </p>
            <div className="flex space-x-4">
              <a href={settings.socialLinks?.facebook || "#"} className="text-gray-400 hover:text-white transition-colors"><Facebook size={20} /></a>
              <a href={settings.socialLinks?.instagram || "#"} className="text-gray-400 hover:text-white transition-colors"><Instagram size={20} /></a>
              <a href={settings.socialLinks?.twitter || "#"} className="text-gray-400 hover:text-white transition-colors"><XIcon size={20} /></a>
              <a href={settings.socialLinks?.linkedin || "#"} className="text-gray-400 hover:text-white transition-colors"><Linkedin size={20} /></a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display text-xl font-bold uppercase tracking-wider mb-6" style={{ color: settings.logoColor }}>Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/inventory" className="text-gray-400 hover:text-white transition-colors text-sm">New Arrivals</Link></li>
              <li><Link to="/financing" className="text-gray-400 hover:text-white transition-colors text-sm">Financing Options</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm">News & Blog</Link></li>
              <li><Link to="/roi" className="text-gray-400 hover:text-white transition-colors text-sm">ROI Calculator</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm">Service & Parts</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm">About Automax</Link></li>

              <li><Link to="/admin" className="text-gray-600 hover:text-gray-400 transition-colors text-xs mt-4 block">Login</Link></li>

              {settings.brochureUrl && (
                <li className="mt-4 pt-4 border-t border-gray-800">
                  <a
                    href={settings.brochureUrl}
                    download="Automax_Company_Profile.pdf"
                    className="flex items-center text-automax-red hover:text-white transition-colors text-sm font-bold gap-2"
                  >
                    <Download size={16} /> Download Brochure
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Service Area */}
          <div>
            <h3 className="font-display text-xl font-bold uppercase tracking-wider mb-6" style={{ color: settings.logoColor }}>Service Area</h3>
            <ul className="space-y-3">
              {settings.serviceAreas && settings.serviceAreas.length > 0 ? (
                settings.serviceAreas.map(area => (
                  <li key={area.id} className="flex items-center text-gray-400 text-sm">
                    <span
                      className={`w-2 h-2 rounded-full mr-2 ${area.isHeadquarters ? '' : 'bg-gray-700'}`}
                      style={area.isHeadquarters ? { backgroundColor: settings.logoColor } : {}}
                    ></span>
                    {area.name} {area.isHeadquarters && '(HQ)'}
                  </li>
                ))
              ) : (
                <>
                  <li className="flex items-center text-gray-400 text-sm"><span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: settings.logoColor }}></span> Dar es Salaam (HQ)</li>
                  <li className="flex items-center text-gray-400 text-sm"><span className="w-2 h-2 bg-gray-700 rounded-full mr-2"></span> Nairobi, Kenya</li>
                  <li className="flex items-center text-gray-400 text-sm"><span className="w-2 h-2 bg-gray-700 rounded-full mr-2"></span> Kampala, Uganda</li>
                  <li className="flex items-center text-gray-400 text-sm"><span className="w-2 h-2 bg-gray-700 rounded-full mr-2"></span> Kigali, Rwanda</li>
                  <li className="flex items-center text-gray-400 text-sm"><span className="w-2 h-2 bg-gray-700 rounded-full mr-2"></span> Bujumbura, Burundi</li>
                </>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-display text-xl font-bold uppercase tracking-wider mb-6" style={{ color: settings.logoColor }}>Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start text-gray-400 text-sm">
                <MapPin className="h-5 w-5 mr-3 flex-shrink-0" style={{ color: settings.logoColor }} />
                <span>{settings.address}</span>
              </div>
              <div className="flex items-center text-gray-400 text-sm">
                <Phone className="h-5 w-5 mr-3 flex-shrink-0" style={{ color: settings.logoColor }} />
                <span>{settings.phoneNumber}</span>
              </div>
              <div className="flex items-center text-gray-400 text-sm">
                <Mail className="h-5 w-5 mr-3 flex-shrink-0" style={{ color: settings.logoColor }} />
                <div className="flex flex-col">
                  <span>{settings.email}</span>
                  {settings.secondaryEmail && <span className="mt-1">{settings.secondaryEmail}</span>}
                </div>
              </div>
              <a
                href={`https://wa.me/${settings.phoneNumber.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-2 rounded-sm font-bold transition-colors mt-2"
              >
                <MessageCircle size={18} className="mr-2" /> WhatsApp Us
              </a>
            </div>
          </div>

        </div>

        <div className="border-t border-gray-900 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} {settings.siteName} Tanzania. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
